import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Download, Loader2, Users } from 'lucide-react';
import api from '../../lib/api';
import { useAuthStore } from '../../lib/auth';
import { Button } from '../../components/ui/button';
import { DriverFilters } from './DriverFilters';
import { TeamKpis } from './TeamKpis';
import { DriversTable } from './DriversTable';
import { DriverCompare } from './DriverCompare';
import { DriverDetailSheet } from './DriverDetailSheet';
import { METRICS, presetRange } from './utils';

const MAX_SELECT = 5;

async function exportExcel(drivers, period) {
  const XLSX = await import('xlsx');
  const headers = ['Şoför', 'Müşteriler', ...METRICS.map((m) => m.label), 'Önceki Rota', 'Önceki Tutar'];
  const rows = drivers.map((d) => [
    d.driver_name, d.customers.join(', '), ...METRICS.map((m) => d[m.key] || 0),
    d.previous?.routes ?? '', d.previous?.revenue ?? '',
  ]);
  const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
  ws['!cols'] = headers.map((h) => ({ wch: Math.max(12, h.length + 2) }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Şoför Analizi');
  XLSX.writeFile(wb, `sofor_analizi_${period.start}_${period.end}.xlsx`);
}

export default function DriverAnalytics() {
  const { user } = useAuthStore();
  const [preset, setPreset] = useState('this_month');
  const [range, setRange] = useState(() => presetRange('this_month'));
  const [customers, setCustomers] = useState([]);
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [firms, setFirms] = useState([]);
  const [firmId, setFirmId] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [compareMode, setCompareMode] = useState('team');
  const [selected, setSelected] = useState([]);
  const [detailDriver, setDetailDriver] = useState(null);
  const isSuper = user?.role === 'SUPER_ADMIN';
  const queryParams = useMemo(() => {
    const p = { start_date: range.start, end_date: range.end };
    if (selectedCustomers.length) p.customers = selectedCustomers.join(',');
    if (firmId) p.firm_id = firmId;
    return p;
  }, [range, selectedCustomers, firmId]);

  useEffect(() => {
    api.get('/customers').then((r) => setCustomers(r.data?.data || [])).catch(() => {});
    if (isSuper) api.get('/firms/').then((r) => setFirms(r.data?.data || [])).catch(() => {});
  }, [isSuper]);

  const load = useCallback(async () => {
    if (!range.start || !range.end) return;
    setLoading(true);
    try {
      const params = { start_date: range.start, end_date: range.end };
      if (selectedCustomers.length) params.customers = selectedCustomers.join(',');
      if (firmId) params.firm_id = firmId;
      const res = await api.get('/routes/driver-analytics', { params });
      setData(res.data?.data || null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Analiz yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [range, selectedCustomers, firmId]);

  useEffect(() => { load(); }, [load]);

  const drivers = useMemo(() => data?.drivers || [], [data]);
  useEffect(() => {
    const keys = new Set(drivers.map((d) => d.driver_key));
    setSelected((s) => (s.every((k) => keys.has(k)) ? s : s.filter((k) => keys.has(k))));
  }, [drivers]);
  const selectedDrivers = useMemo(() => selected.map((k) => drivers.find((d) => d.driver_key === k)).filter(Boolean), [selected, drivers]);
  const toggleSelect = (key) => setSelected((s) => (s.includes(key) ? s.filter((k) => k !== key) : s.length >= MAX_SELECT ? s : [...s, key]));

  return (
    <div className="space-y-5" data-testid="driver-analytics-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white flex items-center gap-2"><Users className="h-7 w-7 text-sky-600" />Şoför Analizi</h1>
          <p className="text-sm text-slate-500 mt-1">Route kayıtlarına göre şoför bazlı performans, kazanç ve kıyaslama</p>
        </div>
        <Button variant="outline" size="sm" disabled={!drivers.length} onClick={() => exportExcel(drivers, data.period)} data-testid="driver-analytics-export">
          <Download className="h-4 w-4 mr-1.5" />Excel
        </Button>
      </div>

      <DriverFilters
        preset={preset}
        onPreset={(k) => { setPreset(k); setRange(presetRange(k)); }}
        range={range}
        onRange={(r) => { setPreset(''); setRange(r); }}
        customers={customers}
        selectedCustomers={selectedCustomers}
        onToggleCustomer={(slug) => setSelectedCustomers((s) => (s.includes(slug) ? s.filter((x) => x !== slug) : [...s, slug]))}
        firms={firms}
        firmId={firmId}
        onFirm={setFirmId}
        showFirm={isSuper}
      />

      {loading && !data ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
      ) : data && (
        <>
          <TeamKpis team={data.team} />
          {selectedDrivers.length >= 1 && <DriverCompare drivers={selectedDrivers} team={data.team} onRemove={toggleSelect} />}
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className="text-slate-500">Tablodaki % göstergesi:</span>
            {[{ k: 'team', l: 'Ekip ortalamasına göre' }, { k: 'prev', l: `Önceki döneme göre (${data.previous_period.start} – ${data.previous_period.end})` }].map((o) => (
              <button key={o.k} type="button" onClick={() => setCompareMode(o.k)} className={`px-2.5 py-1 rounded-full border font-medium transition-colors ${compareMode === o.k ? 'bg-sky-600 text-white border-sky-600' : 'border-slate-200 text-slate-600 hover:border-slate-400 dark:border-slate-700 dark:text-slate-300'}`} data-testid={`compare-mode-${o.k}`}>
                {o.l}
              </button>
            ))}
            {loading && <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400" />}
          </div>
          <DriversTable drivers={drivers} team={data.team} compareMode={compareMode} selected={selected} onToggle={toggleSelect} maxSelect={MAX_SELECT} onOpen={setDetailDriver} />
          <DriverDetailSheet driver={detailDriver} params={queryParams} onClose={() => setDetailDriver(null)} />
        </>
      )}
    </div>
  );
}
